import type { BookingflowStateMachine } from '../../bookingflowstatemachine'
import { State } from '../../states/state'
import ConfirmCancellationAdminState from '../confirmCancellationAdminState'
import type { ConfirmCancellationAdminStateDependencies } from '../confirmCancellationAdminState/confirmCancellationAdminState.types'
import {
    CancelBookingAdminActions,
    FeeSize,
    IGetData,
    OnBehalfOf,
    cancelBookingAdminActions,
    cancelBookingAdminStateActions,
} from './cancelBookingAdminState.types'

const CANCEL_BOOKING_ADMIN_STATE = 'CANCEL_BOOKING_ADMIN_STATE'

export class CancelBookingAdminState extends State<
    CancelBookingAdminActions,
    typeof CANCEL_BOOKING_ADMIN_STATE
> {
    public readonly type = CANCEL_BOOKING_ADMIN_STATE

    private _isCancellationFee = true
    private _cancellationFeeSize: FeeSize = 0
    private _shouldCloseTime = true
    private _cancelOnBehalfOf: OnBehalfOf = 'Customer'
    private _shouldSendCustomerMessages = true
    private _shouldSendPartnerMessages = true
    private _cancelReason = ''

    constructor(bookingflowStateMachine: BookingflowStateMachine) {
        super(bookingflowStateMachine)
    }

    private nextState = ():
        | CancelBookingAdminState
        | ConfirmCancellationAdminState => {
        return new ConfirmCancellationAdminState(
            this.bookingflowStateMachine,
            this.confirmCancellationAdminStateDeps
        )
    }

    private get confirmCancellationAdminStateDeps(): ConfirmCancellationAdminStateDependencies {
        return {
            cancelReason: this._cancelReason,
            cancellationFeeSize: this._cancellationFeeSize,
            cancelOnBehalfOf: this._cancelOnBehalfOf,
            isCancellationFee: this._isCancellationFee,
            shouldCloseTime: this._shouldCloseTime,
            shouldSendCustomerMessages: this._shouldSendCustomerMessages,
            shouldSendPartnerMessages: this._shouldSendPartnerMessages,
            ...(this.bookingflowStateMachine.suggestiveChoices !== 'EMPTY' &&
                this.bookingflowStateMachine.suggestiveChoices),
        }
    }

    /**
     *
     * @param action - The action that is given
     * On the addressState there are no values dependent on
     */
    public unset = (): void => {
        return
    }

    public doAction(
        action: CancelBookingAdminActions
    ): CancelBookingAdminState | ConfirmCancellationAdminState {
        switch (action.type) {
            case 'SET_CANCEL_REASON':
                this._cancelReason = action.data
                return this
            case 'SET_SHOULD_SEND_CUSTOMER_MESSAGES':
                this._shouldSendCustomerMessages = action.data
                return this
            case 'SET_SHOULD_SEND_PARTNER_MESSAGES':
                this._shouldSendPartnerMessages = action.data
                return this
            case 'SET_CANCEL_ON_BEHALF_OF':
                this._cancelOnBehalfOf = action.data
                return this
            case 'SET_SHOULD_CLOSE_TIME':
                this._shouldCloseTime = action.data
                return this
            case 'SET_CANCELLATION_FEE_SIZE':
                this._cancellationFeeSize = action.data
                return this
            case 'SET_IS_CANCELLATION_FEE':
                this._isCancellationFee = action.data
                return this
            case 'NEXT_STATE':
                return this.nextState()
            default:
                throw new Error('These are not the droids you are looking for')
        }
    }

    public getActions = (): cancelBookingAdminStateActions[] =>
        cancelBookingAdminActions.filter((action) =>
            new action(this).isDoable()
        )

    public get cancellationFeeSize(): FeeSize {
        return this._cancellationFeeSize
    }
    public get shouldCloseTime(): boolean {
        return this._shouldCloseTime
    }
    public get cancelOnBehalfOf(): OnBehalfOf {
        return this._cancelOnBehalfOf
    }
    public get shouldSendCustomerMessages(): boolean {
        return this._shouldSendCustomerMessages
    }
    public get shouldSendPartnerMessages(): boolean {
        return this._shouldSendPartnerMessages
    }
    public get cancelReason(): string {
        return this._cancelReason
    }

    public getData(): IGetData {
        return {
            cancellationFeeSize: this.cancellationFeeSize,
            shouldCloseTime: this.shouldCloseTime,
            cancelOnBehalfOf: this.cancelOnBehalfOf,
            shouldSendCustomerMessages: this.shouldSendCustomerMessages,
            shouldSendPartnerMessages: this.shouldSendPartnerMessages,
            cancelReason: this.cancelReason,
        }
    }
}
